import { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { useManejo, useRegistrarManejo, useTalhao } from '../api';
import type { ManejoAplicacao } from '../schemas';
import { manejoLabel } from '../manejo-cats';

function fmtData(s: string): { dia: string; mes: string } {
  try {
    const d = new Date(`${s}T00:00:00`);
    return {
      dia: String(d.getDate()).padStart(2, '0'),
      mes: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
    };
  } catch {
    return { dia: '--', mes: '' };
  }
}

// Telas 06-10 · Manejo por categoria (Adubação/Herbicidas/Fungicidas/Inseticidas/Nutrição).
// Mesma estrutura para as 5 — lista de aplicações na safra do talhão.
export function ManejoView({ talhaoId, categoria }: { talhaoId: number; categoria: string }) {
  const { data: t } = useTalhao(talhaoId);
  const { data: aplicacoes, isLoading } = useManejo(talhaoId, categoria);
  const [modalAberto, setModalAberto] = useState(false);

  const titulo = manejoLabel(categoria);
  const sub = [t?.talhao_nome || (t ? `Talhão ${t.numero}` : ''), t?.fazenda].filter(Boolean).join(' · ');
  const lista = aplicacoes ?? [];

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex flex-col px-3 pt-4 pb-3 shrink-0">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">{titulo}</h1>
        </div>
        {sub && <span className="pl-9 text-[0.85rem] text-text-2">{sub}</span>}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40">
        <p className="pb-2 pt-1 text-[0.72rem] font-semibold uppercase tracking-wider text-accent">
          {lista.length === 0
            ? 'Aplicações na safra'
            : `${lista.length} ${lista.length === 1 ? 'aplicação' : 'aplicações'} na safra`}
        </p>

        {isLoading && <p className="py-8 text-center text-sm text-text-muted">Carregando…</p>}
        {!isLoading && lista.length === 0 && (
          <div className="rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-10 text-center">
            <p className="text-[0.9rem] font-medium text-text">Nenhuma aplicação registrada</p>
            <p className="mt-1 text-[0.8rem] text-text-2">Toque em “Registrar aplicação” para começar.</p>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {lista.map((a) => (
            <AplicacaoRow key={a.id} a={a} />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setModalAberto(true)}
        className="absolute bottom-24 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] shadow-[var(--shadow-over)]"
      >
        <Plus size={20} strokeWidth={2.25} /> Registrar aplicação
      </button>

      {modalAberto && (
        <RegistrarModal talhaoId={talhaoId} categoria={categoria} onClose={() => setModalAberto(false)} />
      )}
    </div>
  );
}

function AplicacaoRow({ a }: { a: ManejoAplicacao }) {
  const { dia, mes } = fmtData(a.data_aplicacao);
  return (
    <div className="flex w-full items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5 text-left">
      <span className="flex size-12 shrink-0 flex-col items-center justify-center rounded-[11px] bg-surface3">
        <span className="text-[0.95rem] font-bold leading-none text-text">{dia}</span>
        <span className="text-[0.6rem] font-semibold uppercase text-text-muted">{mes}</span>
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[0.9rem] font-medium text-text">{a.titulo || 'Aplicação'}</span>
        {a.detalhe && <span className="truncate text-[0.8rem] text-text-2">{a.detalhe}</span>}
      </div>
      <ChevronRight size={16} strokeWidth={2} className="shrink-0 text-[#565350]" />
    </div>
  );
}

function RegistrarModal({
  talhaoId,
  categoria,
  onClose,
}: {
  talhaoId: number;
  categoria: string;
  onClose: () => void;
}) {
  const reg = useRegistrarManejo(talhaoId, categoria);
  const [data, setData] = useState('');
  const [titulo, setTitulo] = useState('');
  const [detalhe, setDetalhe] = useState('');

  const campo = 'rounded-xl border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5 text-[0.9rem] text-text placeholder:text-text-muted';

  return (
    <div className="absolute inset-0 z-20 flex items-end bg-black/30" onClick={onClose}>
      <div className="w-full rounded-t-3xl bg-surface p-5 pb-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[1.05rem] font-bold text-text">Registrar aplicação</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex size-8 items-center justify-center rounded-full text-text-2 hover:bg-surface2"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-text-2">Data</span>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} className={campo} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-text-2">Descrição</span>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Adubação de base"
              className={campo}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-text-2">Dose / produto</span>
            <input
              value={detalhe}
              onChange={(e) => setDetalhe(e.target.value)}
              placeholder="Ex.: 350 kg/ha · 02-20-18 · linha"
              className={campo}
            />
          </label>
          {reg.isError && <p className="text-[0.8rem] text-[var(--danger)]">Não foi possível salvar agora.</p>}
          <button
            type="button"
            disabled={!data || !titulo || reg.isPending}
            onClick={() =>
              reg.mutate({ data_aplicacao: data, titulo, detalhe: detalhe || null }, { onSuccess: onClose })
            }
            className="mt-2 rounded-full bg-[var(--accent)] py-3 text-[0.92rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
          >
            {reg.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
